
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfileForm } from "@/components/profile/ProfileForm";
import { EmailForm } from "@/components/profile/EmailForm";
import { PasswordForm } from "@/components/profile/PasswordForm";
import { ExternalContactForm } from "@/components/profile/ExternalContactForm";
import { Shield } from "lucide-react";

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("");
  const [residenceCountry, setResidenceCountry] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [telegramUsername, setTelegramUsername] = useState<string | null>(null);
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  const [preferredPlatform, setPreferredPlatform] = useState("none");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    getProfile();
  }, []);

  const getProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("username, residence_country, phone_number, whatsapp_enabled, telegram_username, telegram_enabled, preferred_messaging_platform")
        .eq("id", user.id)
        .single();

      if (error) throw error;

      if (data) {
        setUsername(data.username);
        setResidenceCountry(data.residence_country);
        setPhoneNumber(data.phone_number);
        setWhatsappEnabled(data.whatsapp_enabled || false);
        setTelegramUsername(data.telegram_username);
        setTelegramEnabled(data.telegram_enabled || false);
        setPreferredPlatform(data.preferred_messaging_platform || "none");
      }
      setEmail(user.email || "");
    } catch (error: any) {
      toast({
        title: t("profile.messages.error"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate("/auth");
    } catch (error: any) {
      toast({
        title: t("profile.messages.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">{t("profile.loading")}</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t("profile.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="profile" className="space-y-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="profile">{t("profile.tabs.profile")}</TabsTrigger>
              <TabsTrigger value="email">{t("profile.tabs.email")}</TabsTrigger>
              <TabsTrigger value="password">{t("profile.tabs.password")}</TabsTrigger>
              <TabsTrigger value="contact">{t("contact.tab")}</TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <ProfileForm 
                username={username}
                email={email}
                residenceCountry={residenceCountry}
                onUsernameChange={setUsername}
                onCountryChange={setResidenceCountry}
              />
            </TabsContent>

            <TabsContent value="email">
              <EmailForm initialEmail={email} />
            </TabsContent>

            <TabsContent value="password">
              <PasswordForm />
            </TabsContent>

            <TabsContent value="contact">
              <ExternalContactForm
                phoneNumber={phoneNumber}
                whatsappEnabled={whatsappEnabled}
                telegramUsername={telegramUsername}
                telegramEnabled={telegramEnabled}
                preferredPlatform={preferredPlatform}
                onUpdate={getProfile}
              />
            </TabsContent>
          </Tabs>

          <div className="mt-6 space-y-3">
            <Button 
              onClick={() => navigate("/gdpr")} 
              variant="outline" 
              className="w-full flex items-center gap-2"
            >
              <Shield className="h-4 w-4" />
              {t("profile.buttons.gdprManagement")}
            </Button>
            
            <Button type="button" variant="outline" onClick={handleSignOut} className="w-full">
              {t("profile.buttons.signOut")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
